import { BaseButton } from '../UI/BaseButton';

interface AppErrorFallbackProps {
  // Sentry.ErrorBoundary passes the error + a reset fn; both optional so this
  // can be reused as a plain fallback elsewhere.
  error?: unknown;
  resetError?: () => void;
}

/**
 * Fallback rendered when a render-time error escapes to an ErrorBoundary.
 * Without this, an uncaught throw (e.g. in the Tiptap editor or the task list)
 * unmounts the tree and leaves a blank white screen. Sentry captures the error
 * automatically via Sentry.ErrorBoundary; here we just give the user a way out.
 */
export const AppErrorFallback = ({ resetError }: AppErrorFallbackProps) => (
  <div className="flex min-h-[60vh] w-full flex-col items-center justify-center gap-4 p-8 text-center">
    <h2 className="text-brand-grey-dark text-xl font-semibold">Something went wrong</h2>
    <p className="text-brand-grey-dark max-w-md text-sm opacity-70">
      An unexpected error occurred while rendering this view. You can try again, or reload the page if the problem
      persists.
    </p>
    <div className="flex gap-3">
      {resetError && (
        <BaseButton type="secondary" size="small" onClick={resetError}>
          Try again
        </BaseButton>
      )}
      <BaseButton type="primary" size="small" onClick={() => window.location.reload()}>
        Reload page
      </BaseButton>
    </div>
  </div>
);
